import { useState } from 'react'
import { Download, MessageCircle, Plus, RotateCcw, ShoppingBag, Trash2 } from 'lucide-react'
import type { CreateLeadSaleRequest, LandingLeadDto, LeadSaleDto } from '@impulso/shared/api/tracking'
import { whatsappUrl } from '@impulso/shared/landing'
import { errorMessage } from '../../lib/api'
import { useCan, useCompany } from '../../lib/company'
import { Empty, ErrorState, Loading } from '../../ui'
import { useConfirm } from '../../ui/confirm'
import { useToast } from '../../ui/toast'
import {
  exportLead, useCancelLeadSale, useCreateLeadSale, useDeleteLead, useLandingLeads,
  useLeadSales, useSetLeadStatus,
} from './api'
import { fmtDateTime } from './components'
import { parseBrlToCents } from './money'
import ManualLeadModal from './ManualLeadModal'

export default function LeadManager() {
  const company = useCompany()
  const query = useLandingLeads(company.id)
  const status = useSetLeadStatus(company.id)
  const remove = useDeleteLead(company.id)
  const canEdit = useCan('company.edit_drafts')
  const canDelete = useCan('company.publish')
  const [creating, setCreating] = useState(false)
  const [selectedLead, setSelectedLead] = useState<LandingLeadDto | null>(null)
  const [expandedLeadId, setExpandedLeadId] = useState<string | null>(null)
  const toast = useToast()
  const confirm = useConfirm()

  if (query.isLoading) return <Loading lines={5} />
  if (query.error) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />

  const leads = query.data!.items
  const rows = selectedLead && !leads.some((lead) => lead.id === selectedLead.id) ? [...leads, selectedLead] : leads
  const pending = rows.filter((lead) => lead.status === 'new').length
  return (
    <div className="stack" style={{ gap: 12 }}>
      <div className="row between wrap" style={{ gap: 8 }}>
        <span className="small muted">{rows.length} contato(s){pending ? ` · ${pending} aguardando retorno` : ''}</span>
        <div className="row wrap" style={{ gap: 8 }}>
          {rows.length > 0 && <button className="btn sm" onClick={() => downloadCsv(rows, company.name)}><Download size={14} /> Baixar planilha</button>}
          {canEdit && <button className="btn primary sm" onClick={() => setCreating(true)}><Plus size={14} /> Novo contato</button>}
        </div>
      </div>
      {rows.length === 0 ? (
        <Empty title="Nenhum contato ainda">
          <p>Contatos dos formulários chegam aqui. Você também pode cadastrar quem chamou pelo WhatsApp.</p>
          {canEdit && <button className="btn primary mt-12" onClick={() => setCreating(true)}><MessageCircle size={15} /> Cadastrar contato do WhatsApp</button>}
        </Empty>
      ) : (
        <div className="card flush">
          <div className="table-wrap">
            <table className="table">
              <thead><tr><th>Recebido</th><th>Nome</th><th>Atendimento</th><th>Origem</th><th>Autorização WhatsApp</th><th>Situação</th><th>Vendas</th><th>Ações</th></tr></thead>
              <tbody>
                {rows.map((lead) => (
                  <LeadRow key={lead.id} lead={lead} companyId={company.id} canEdit={canEdit} canDelete={canDelete}
                    statusPending={status.isPending} deletePending={remove.isPending}
                    expanded={expandedLeadId === lead.id} onToggleExpanded={() => setExpandedLeadId(expandedLeadId === lead.id ? null : lead.id)}
                    onStatus={() => status.mutate({ id: lead.id, status: lead.status === 'new' ? 'contacted' : 'new' }, {
                      onSuccess: () => { if (selectedLead?.id === lead.id) setSelectedLead({ ...selectedLead, status: lead.status === 'new' ? 'contacted' : 'new' }) },
                      onError: (e) => toast(errorMessage(e)),
                    })}
                    onDelete={async () => {
                      if (await confirm({ title: 'Apagar este contato para sempre?', message: 'As vendas vinculadas também serão excluídas. Os dados enviados ao Google ou à Meta precisam ser removidos por lá.', confirmLabel: 'Apagar', danger: true })) {
                        remove.mutate(lead.id, { onSuccess: () => { if (selectedLead?.id === lead.id) setSelectedLead(null) }, onError: (e) => toast(errorMessage(e)) })
                      }
                    }} />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {creating && <ManualLeadModal companyId={company.id} onClose={() => setCreating(false)} onUseExisting={(lead) => {
        setSelectedLead(lead)
        setExpandedLeadId(lead.id)
        setCreating(false)
        toast('Contato existente selecionado; os dados foram mantidos.')
      }} />}
    </div>
  )
}

function LeadRow({ lead, companyId, canEdit, canDelete, statusPending, deletePending, expanded, onToggleExpanded, onStatus, onDelete }: {
  lead: LandingLeadDto
  companyId: string
  canEdit: boolean
  canDelete: boolean
  statusPending: boolean
  deletePending: boolean
  expanded: boolean
  onToggleExpanded: () => void
  onStatus: () => void
  onDelete: () => void
}) {
  const [showSaleForm, setShowSaleForm] = useState(false)
  const sales = useLeadSales(companyId, expanded ? lead.id : null)
  const toast = useToast()
  return (
    <>
      <tr>
        <td className="nowrap small">{fmtDateTime(lead.createdAt)}</td>
        <td className="strong">{lead.name ?? '—'}</td>
        <td className="small">
          {lead.phone ? <a className="btn sm ghost" href={whatsappUrl(lead.phone, `Olá${lead.name ? `, ${lead.name.split(' ')[0]}` : ''}! Vamos continuar nosso atendimento por aqui?`)} target="_blank" rel="noreferrer"><MessageCircle size={13} /> Abrir WhatsApp</a> : '—'}
          {lead.email && <div className="mt-8"><a href={`mailto:${lead.email}`} style={{ color: 'var(--primary)' }}>{lead.email}</a></div>}
          {lead.message && <details className="mt-8 small"><summary>Anotação do atendimento</summary><p>{lead.message}</p></details>}
        </td>
        <td className="small" title={lead.pageTitle ?? undefined}>
          {lead.source ?? '—'}{lead.pageTitle && <div className="muted">{lead.pageTitle}</div>}
          {lead.forwarded && <div className="row wrap mt-8" style={{ gap: 4 }}>{(['meta', 'google'] as const).map((platform) => lead.forwarded?.[platform] && (
            <span key={platform} className={`badge ${lead.forwarded[platform]!.status === 'sent' ? 'green' : lead.forwarded[platform]!.status === 'error' ? 'red' : ''}`} title={lead.forwarded[platform]!.detail ?? ''}>
              {platform === 'meta' ? 'Meta' : 'Google'} {lead.forwarded[platform]!.status === 'sent' ? '✓' : lead.forwarded[platform]!.status === 'error' ? '!' : '–'}
            </span>
          ))}</div>}
        </td>
        <td className="small">{lead.whatsappConsent ? <span className="badge green">Registrada</span> : <span className="muted">Não registrada</span>}</td>
        <td><button className={`badge ${lead.status === 'new' ? 'amber' : 'green'}`} style={{ cursor: 'pointer' }} disabled={statusPending} onClick={onStatus} title="Clique para mudar">{lead.status === 'new' ? 'Novo' : 'Respondido'}</button></td>
        <td><button className="btn sm" onClick={() => { onToggleExpanded(); setShowSaleForm(false) }}><ShoppingBag size={13} /> {expanded ? 'Fechar' : 'Ver vendas'}</button></td>
        <td className="nowrap">
          <button className="btn sm ghost" title="Baixar uma cópia de tudo o que guardamos desta pessoa" onClick={() => exportLead(companyId, lead.id).catch((e) => toast(errorMessage(e)))}><Download size={13} /> Exportar</button>
          {canDelete && <button className="btn sm ghost" disabled={deletePending} title="Apagar este contato" onClick={onDelete}><Trash2 size={13} /> Apagar</button>}
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={8}>
            <SalesPanel lead={lead} companyId={companyId} canEdit={canEdit} sales={sales.data?.items ?? []} loading={sales.isLoading}
              error={sales.error} onRetry={() => void sales.refetch()} showSaleForm={showSaleForm} onShowSaleForm={() => setShowSaleForm(true)} onHideSaleForm={() => setShowSaleForm(false)} />
          </td>
        </tr>
      )}
    </>
  )
}

function SalesPanel({ lead, companyId, canEdit, sales, loading, error, onRetry, showSaleForm, onShowSaleForm, onHideSaleForm }: {
  lead: LandingLeadDto
  companyId: string
  canEdit: boolean
  sales: LeadSaleDto[]
  loading: boolean
  error: unknown
  onRetry: () => void
  showSaleForm: boolean
  onShowSaleForm: () => void
  onHideSaleForm: () => void
}) {
  const totalCents = sales.reduce((sum, sale) => sum + (sale.status === 'confirmed' ? sale.amountCents : 0), 0)
  return (
    <div className="card stack" style={{ gap: 10, background: 'var(--surface-subtle, #f8fafc)' }}>
      <div className="row between wrap" style={{ gap: 8 }}>
        <div>
          <b>Receita registrada: {formatCents(totalCents)}</b>
          <div className="small muted">Somente vendas informadas manualmente; não é estimativa de mídia. Estornos ficam no histórico e saem do total.</div>
        </div>
        {canEdit && <button className="btn primary sm" onClick={onShowSaleForm}><Plus size={14} /> Registrar venda</button>}
      </div>
      {loading && <Loading lines={2} />}
      {!!error && <div className="row wrap"><span className="form-error">Não foi possível carregar as vendas.</span><button className="btn sm" onClick={onRetry}>Tentar de novo</button></div>}
      {!loading && !error && sales.length === 0 && <p className="small muted">Nenhuma venda registrada para este contato.</p>}
      {!loading && !error && sales.map((sale) => <SaleRow key={sale.id} lead={lead} companyId={companyId} sale={sale} />)}
      {showSaleForm && <SaleForm leadId={lead.id} companyId={companyId} onClose={onHideSaleForm} />}
      <p className="small muted">Para corrigir um lançamento, estorne-o e registre a venda correta.</p>
    </div>
  )
}

function SaleRow({ lead, companyId, sale }: { lead: LandingLeadDto; companyId: string; sale: LeadSaleDto }) {
  const cancel = useCancelLeadSale(companyId, lead.id)
  const confirm = useConfirm()
  const toast = useToast()
  const cancelled = sale.status === 'cancelled'
  return (
    <div className="row between wrap small" style={{ gap: 8 }}>
      <span className={cancelled ? 'muted' : ''}>
        <b>{sale.kind === 'sale' ? 'Venda' : 'Recompra'}</b> · {formatSaleDate(sale.soldAt)} · {formatCents(sale.amountCents)}
        {cancelled && <span className="badge red ml-8">Estornada · fora do total</span>}
      </span>
      {!cancelled && <button className="btn sm ghost" disabled={cancel.isPending} onClick={async () => {
        if (await confirm({ title: 'Estornar esta venda?', message: 'O registro continuará no histórico e deixará de compor a receita registrada.', confirmLabel: 'Estornar', danger: true })) {
          cancel.mutate(sale.id, { onError: (e) => toast(errorMessage(e)) })
        }
      }}><RotateCcw size={13} /> Estornar</button>}
    </div>
  )
}

function SaleForm({ leadId, companyId, onClose }: { leadId: string; companyId: string; onClose: () => void }) {
  const create = useCreateLeadSale(companyId, leadId)
  const toast = useToast()
  const [kind, setKind] = useState<CreateLeadSaleRequest['kind']>('sale')
  const [amount, setAmount] = useState('')
  const [soldAt, setSoldAt] = useState(localDate())
  const [invalidAmount, setInvalidAmount] = useState(false)
  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    const amountCents = parseBrlToCents(amount)
    if (!amountCents) { setInvalidAmount(true); return }
    create.mutate({ kind, amountCents, soldAt }, { onSuccess: () => { toast('Venda registrada.'); onClose() }, onError: (e) => toast(errorMessage(e)) })
  }
  return (
    <form className="card stack" style={{ gap: 10 }} onSubmit={submit}>
      <div className="grid g-3" style={{ gap: 10 }}>
        <div className="field"><label htmlFor={`sale-kind-${leadId}`}>Tipo</label><select id={`sale-kind-${leadId}`} className="input" value={kind} onChange={(e) => setKind(e.target.value as CreateLeadSaleRequest['kind'])}><option value="sale">Venda</option><option value="repurchase">Recompra</option></select></div>
        <div className="field"><label htmlFor={`sale-amount-${leadId}`}>Valor (R$)</label><input id={`sale-amount-${leadId}`} className="input" type="text" inputMode="decimal" placeholder="0,00" value={amount} onChange={(e) => { setAmount(e.target.value); setInvalidAmount(false) }} aria-invalid={invalidAmount} /></div>
        <div className="field"><label htmlFor={`sale-date-${leadId}`}>Data da venda</label><input id={`sale-date-${leadId}`} className="input" type="date" value={soldAt} onChange={(e) => setSoldAt(e.target.value)} required /></div>
      </div>
      {invalidAmount && <div className="form-error" role="alert">Informe um valor maior que zero, com até duas casas decimais.</div>}
      <div className="row wrap" style={{ gap: 8 }}><button type="button" className="btn ghost sm" onClick={onClose}>Cancelar</button><button className="btn primary sm" disabled={create.isPending}>{create.isPending ? 'Salvando…' : 'Salvar venda'}</button></div>
    </form>
  )
}

function formatCents(cents: number) {
  return (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

function formatSaleDate(date: string) {
  return new Date(`${date}T12:00:00`).toLocaleDateString('pt-BR')
}

function localDate() {
  const date = new Date()
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}

function downloadCsv(items: LandingLeadDto[], companyName: string) {
  const esc = (value: string | null) => `"${(value ?? '').replace(/"/g, '""')}"`
  const head = ['Recebido em', 'Nome', 'Telefone', 'E-mail', 'Mensagem', 'Página', 'Origem', 'Autorização WhatsApp', 'Situação']
  const rows = items.map((lead) => [fmtDateTime(lead.createdAt), lead.name, lead.phone, lead.email, lead.message, lead.pageTitle, lead.source, lead.whatsappConsent ? 'Registrada' : 'Não registrada', lead.status === 'new' ? 'Novo' : 'Respondido'].map(esc).join(';'))
  const blob = new Blob([`﻿${head.join(';')}\n${rows.join('\n')}`], { type: 'text/csv;charset=utf-8' })
  const anchor = document.createElement('a')
  anchor.href = URL.createObjectURL(blob)
  anchor.download = `contatos-${companyName.toLowerCase().replace(/[^a-z0-9]+/g, '-')}.csv`
  anchor.click()
  URL.revokeObjectURL(anchor.href)
}
